import { ThemeEvents } from '@theme/events';

const UTM_ID_KEY = 'utm_id';
const DEFAULT_UTM_ID = 'WBST';
const STORAGE_KEY = 'utm_id';
const SESSION_STORAGE_KEY = 'utm_id_pending';
const CUSTOMER_PRIVACY_API_VERSION = '0.1';
const CART_UPDATE_DELAY_MS = 100;
const MAX_UTM_ID_LENGTH = 255;

let updateCartAttributesTimeout = null;

function hasConsent() {
  try {
    if (!window.Shopify?.customerPrivacy) {
      return false;
    }
    return window.Shopify.customerPrivacy.marketingAllowed() || 
           window.Shopify.customerPrivacy.analyticsProcessingAllowed();
  } catch {
    return false;
  }
}

function captureUTMId() {
  try {
    const utmId = new URLSearchParams(window.location.search).get(UTM_ID_KEY);
    return utmId ? utmId.trim() : null;
  } catch {
    return null;
  }
}

function isValidUTMId(utmId) {
  return utmId && typeof utmId === 'string' && utmId.trim().length > 0 && utmId.trim().length <= MAX_UTM_ID_LENGTH;
}

function storePendingUTMId(utmId) {
  if (!isValidUTMId(utmId)) return;
  try {
    sessionStorage.setItem(SESSION_STORAGE_KEY, utmId);
  } catch {}
}

function getPendingUTMId() {
  try {
    return sessionStorage.getItem(SESSION_STORAGE_KEY);
  } catch {
    return null;
  }
}

function clearPendingUTMId() {
  try {
    sessionStorage.removeItem(SESSION_STORAGE_KEY);
  } catch {}
}

function storeUTMId(utmId) {
  if (!isValidUTMId(utmId)) return;

  if (!hasConsent()) {
    try {
      localStorage.removeItem(STORAGE_KEY);
      clearPendingUTMId();
    } catch {}
    return;
  }

  try {
    localStorage.setItem(STORAGE_KEY, utmId);
    clearPendingUTMId();
  } catch {}
}

function getStoredUTMId() {
  try {
    return localStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function getUTMIdForCart() {
  if (!hasConsent()) {
    return null;
  }
  const stored = getStoredUTMId();
  if (stored) return stored;
  return DEFAULT_UTM_ID;
}

function getCartAttributes() {
  return {
    attributes: {
      [UTM_ID_KEY]: getUTMIdForCart()
    }
  };
}

async function updateCartAttributes() {
  if (!hasConsent()) {
    return Promise.resolve(null);
  }

  if (updateCartAttributesTimeout) {
    clearTimeout(updateCartAttributesTimeout);
  }

  return new Promise((resolve) => {
    updateCartAttributesTimeout = setTimeout(async () => {
      try {
        const cartCheck = await fetch('/cart.js');
        if (!cartCheck.ok) {
          resolve(null);
          return;
        }

        const cartAttributes = getCartAttributes();
        const response = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(cartAttributes),
        });

        if (!response.ok) {
          resolve(null);
          return;
        }

        resolve(await response.json());
      } catch {
        resolve(null);
      }
    }, CART_UPDATE_DELAY_MS);
  });
}

function processUTMId(utmId) {
  if (!isValidUTMId(utmId)) return;
  if (hasConsent()) {
    storeUTMId(utmId);
  } else {
    // Don't store pending UTM ID if consent is not given
    clearPendingUTMId();
  }
}

function initUTMTracking() {
  const utmId = captureUTMId();
  
  if (window.Shopify?.loadFeatures) {
    window.Shopify.loadFeatures(
      [{ name: 'consent-tracking-api', version: CUSTOMER_PRIVACY_API_VERSION }],
      (error) => {
        if (!error && utmId) {
          processUTMId(utmId);
        }
        // Only update cart if consent is given
        if (hasConsent()) {
          updateCartAttributes();
        }
      }
    );
  } else if (window.Shopify?.customerPrivacy) {
    if (utmId) {
      processUTMId(utmId);
    }
    // Only update cart if consent is given
    if (hasConsent()) {
      updateCartAttributes();
    }
  } else {
    // No consent API available, don't track
    if (hasConsent()) {
      updateCartAttributes();
    }
  }
}

function handleCartAdd() {
  updateCartAttributes();
}

function handleCartUpdate() {
  updateCartAttributes();
}

function handleVisitorConsentCollected() {
  const pendingUtmId = getPendingUTMId();
  const storedUtmId = getStoredUTMId();
  const currentUrlUtmId = captureUTMId();

  if (!hasConsent()) {
    try {
      localStorage.removeItem(STORAGE_KEY);
      clearPendingUTMId();
    } catch {}
    // Don't update cart if consent is not given
    return;
  } else {
    const utmIdToStore = currentUrlUtmId || pendingUtmId || storedUtmId;
    if (utmIdToStore && isValidUTMId(utmIdToStore)) {
      storeUTMId(utmIdToStore);
    }
  }
  // Only update cart if consent is given
  updateCartAttributes();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initUTMTracking);
} else {
  initUTMTracking();
}

document.addEventListener(ThemeEvents.cartAdd, handleCartAdd);
document.addEventListener(ThemeEvents.cartUpdate, handleCartUpdate);
document.addEventListener('visitorConsentCollected', handleVisitorConsentCollected);

if (window.Shopify?.customerPrivacy) {
  window.Shopify.customerPrivacy.on?.('visitorConsentCollected', handleVisitorConsentCollected);
}

if (typeof window !== 'undefined') {
  window.UTMTracking = {
    captureUTMId,
    getStoredUTMId,
    getPendingUTMId,
    getUTMIdForCart,
    updateCartAttributes,
    hasConsent,
    processUTMId,
  };
}
